import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Play, Pause, SkipForward, SkipBack, Radio as RadioIcon, BookOpen, Mic2 } from "lucide-react";

export default function NocturneRadio() {
  const [tracks, setTracks] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const tickRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/radio").then(r => setTracks(r.data)).catch(()=>{});
  }, []);

  const current = tracks[idx];
  const hasAudio = !!current?.audio_url;

  const fmt = (secs) => {
    if (!secs || !isFinite(secs)) return null;
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  // Simulated ticker — only for tracks without a real audio file
  useEffect(() => {
    if (playing && !hasAudio) {
      tickRef.current = setInterval(() => {
        setProgress(p => {
          if (p >= 100) {
            setIdx(i => (i + 1) % Math.max(tracks.length, 1));
            return 0;
          }
          return p + 0.6;
        });
      }, 220);
    } else {
      clearInterval(tickRef.current);
    }
    return () => clearInterval(tickRef.current);
  }, [playing, tracks.length, hasAudio]);

  // Real audio playback control
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !hasAudio) return;
    if (playing) {
      el.play().catch(() => setPlaying(false));
    } else {
      el.pause();
    }
  }, [playing, idx, hasAudio]);

  useEffect(() => { setProgress(0); setAudioDuration(0); }, [idx]);

  const onTimeUpdate = () => {
    const el = audioRef.current;
    if (el && el.duration) setProgress((el.currentTime / el.duration) * 100);
  };

  const onLoadedMetadata = () => {
    const el = audioRef.current;
    if (el) setAudioDuration(el.duration);
  };

  const onEnded = () => {
    if (tracks.length) setIdx((idx + 1) % tracks.length);
  };

  const skip = (dir) => {
    if (!tracks.length) return;
    setIdx((idx + dir + tracks.length) % tracks.length);
  };

  const durationLabel = hasAudio ? (fmt(audioDuration) || "—:—") : (current?.duration || "0:00");

  return (
    <div className="page-container fade-in" data-testid="radio-page">
      <div className="page-eyebrow">a fallout-inspired transmission</div>
      <h1 className="page-title">Nocturne Radio</h1>
      <p className="page-sub">A wandering tower at the edge of the wasteland broadcasts each night. Tune in — the static is part of the show.</p>

      <div className="parchment-card p-6 md:p-10 mt-8 relative overflow-hidden" data-testid="radio-player">
        <div className="absolute -top-12 -right-12 lantern-glow" style={{width:"320px", height:"320px"}}/>
        <div className="flex items-start gap-5 flex-wrap">
          <div className="h-24 w-24 grid place-items-center rounded-full border" style={{borderColor:"var(--gold)", background:"rgba(0,0,0,0.3)"}}>
            <RadioIcon className="h-10 w-10" style={{color:"var(--gold)"}} />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="page-eyebrow">now playing</div>
            <div className="tavern-heading text-3xl gold-text mt-1">{current?.title || "Tuning the dial…"}</div>
            <div className="tavern-script text-xl mt-1" style={{color:"var(--orchid-light)"}}>with DJ {current?.dj || "—"}</div>

            <div className="mt-5 h-1 rounded" style={{background:"rgba(255,255,255,0.1)"}}>
              <div className="h-1 rounded transition-all" style={{width:`${progress}%`, background:"var(--gold)", boxShadow:"0 0 10px rgba(212,175,55,0.7)"}}/>
            </div>
            <div className="flex items-center justify-between text-xs mt-2" style={{color:"var(--muted)"}}>
              <span>broadcasting</span>
              <span>{durationLabel}</span>
            </div>

            <div className="flex items-center gap-3 mt-5">
              <button className="gold-btn" onClick={() => skip(-1)} aria-label="Previous" data-testid="radio-prev"><SkipBack className="h-4 w-4"/></button>
              <button className="gold-btn glow-pulse" onClick={() => setPlaying(p => !p)} aria-label="Play/Pause" data-testid="radio-playpause">
                {playing ? <Pause className="h-4 w-4"/> : <Play className="h-4 w-4"/>}
                {playing ? "Pause" : "Tune In"}
              </button>
              <button className="gold-btn" onClick={() => skip(1)} aria-label="Next" data-testid="radio-next"><SkipForward className="h-4 w-4"/></button>
            </div>

            <audio
              ref={audioRef}
              src={hasAudio ? current.audio_url : undefined}
              onTimeUpdate={onTimeUpdate}
              onLoadedMetadata={onLoadedMetadata}
              onEnded={onEnded}
              preload="metadata"
              className="hidden"
              data-testid="radio-audio"
            />

            {!hasAudio && (
              <div className="text-xs mt-3" style={{color:"var(--muted)"}}>
                ✱ This transmission has no audio file yet. Upload an MP3 from the keeper's quarters to broadcast it here.
              </div>
            )}
          </div>
        </div>

        {current?.lore && (
          <div className="mt-7 border-l-2 pl-4" style={{borderColor:"var(--gold)"}}>
            <div className="page-eyebrow">field note</div>
            <p className="mt-1 leading-relaxed" style={{color:"var(--silver)"}}>{current.lore}</p>
          </div>
        )}
      </div>

      <div className="parchment-card p-6 md:p-8 mt-8" data-testid="spotify-section">
        <div className="page-eyebrow">curated by the tavern</div>
        <h2 className="tavern-heading text-2xl gold-text mt-1">Now Playing in the Tavern</h2>
        <p className="text-sm mt-2" style={{color:"var(--muted)"}}>
          A drifting playlist for the hearth. Let it play while you wander the rooms.
        </p>
        <div className="mt-5">
          <iframe
            data-testid="embed-iframe"
            title="Now Playing in the Tavern"
            style={{borderRadius:"12px"}}
            src="https://open.spotify.com/embed/playlist/47KSTGVWrkVnokgMi9Xv9h?utm_source=generator&si=3b5c7efbb5974ed8"
            width="100%"
            height="352"
            frameBorder="0"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <div className="parchment-card p-6" data-testid="radio-tracklist">
          <h2 className="tavern-heading text-2xl gold-text">Tonight's Setlist</h2>
          <ul className="mt-4 space-y-2">
            {tracks.map((t, i) => (
              <li key={t.id}>
                <button
                  onClick={() => { setIdx(i); setPlaying(true); }}
                  className={`w-full text-left p-3 rounded-sm flex items-center justify-between transition-colors ${i === idx ? "bg-[rgba(212,175,55,0.1)]" : "hover:bg-[rgba(255,255,255,0.04)]"}`}
                  data-testid={`radio-track-${i}`}
                >
                  <div>
                    <div className="tavern-heading text-base" style={{color: i === idx ? "var(--gold)" : "var(--silver)"}}>{i+1}. {t.title}</div>
                    <div className="text-xs" style={{color:"var(--muted)"}}>DJ {t.dj} · {t.duration}</div>
                  </div>
                  {i === idx && playing && <span className="live-dot" />}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="parchment-card p-6" data-testid="radio-djs">
          <h2 className="tavern-heading text-2xl gold-text flex items-center gap-2"><Mic2 className="h-5 w-5"/> The DJs</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {[
              {name:"Nocturne", line:"Pilot host. Reads field journals between songs."},
              {name:"Stardust", line:"Late night fiddler. Hosts the Orchid Hour."},
            ].map((dj,i) => (
              <div key={i} className="p-4 rounded-sm border" style={{borderColor:"rgba(212,175,55,0.22)", background:"rgba(0,0,0,0.2)"}}>
                <div className="tavern-heading text-lg gold-text">{dj.name}</div>
                <div className="text-xs mt-1" style={{color:"var(--muted)"}}>{dj.line}</div>
              </div>
            ))}
          </div>

          <h3 className="section-title mt-6 flex items-center gap-2"><BookOpen className="h-4 w-4" style={{color:"var(--gold)"}}/> Lore Archive</h3>
          <p className="text-sm mt-2" style={{color:"var(--muted)"}}>
            The Tower drifts between three settlements depending on the season. Listeners send field notes via raven.
            Each episode opens with a weather report from somewhere imaginary.
          </p>
        </div>
      </div>
    </div>
  );
}
